import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-mini-calculator',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoPipe],
  templateUrl: './mini-calculator.component.html',
  styleUrl: './mini-calculator.component.css'
})
export class MiniCalculatorComponent {
  @Input() disabled = false;

  @Input() prefill: string | null = null;

  @Output() accept = new EventEmitter<number>();

  @ViewChild('calcInput') calcInputRef?: ElementRef<HTMLInputElement>;

  @Output() trackCalcExprHistoryChange = new EventEmitter<string | null>();

  showCalc = false;
  calcExpr = '=';
  calcPreview: string | null = null;

  trackCalcExprHistory: string | null = null;
  trackCaclResultHistory: number | null = null;

  toggleCalc(ev?: Event) {
    ev?.stopPropagation();
    if (this.disabled) return;

    var trimmed = (this.prefill || '').trim();

    if (trimmed && !trimmed.startsWith('=')) {
      const normalizedPrefill = trimmed.replace(',', '.');
      const normalizedHistory = this.trackCaclResultHistory?.toString().replace(',', '.');
      if (this.trackCaclResultHistory != null && normalizedPrefill === normalizedHistory && this.trackCalcExprHistory) {
        this.calcExpr = this.trackCalcExprHistory;
      }
      else {
        this.calcExpr = '=' + trimmed;
      }
    } else if (!this.calcExpr) {
      this.calcExpr = '=';
    }

    this.updateCalcPreview();
    this.showCalc = !this.showCalc;
    setTimeout(() => this.calcInputRef?.nativeElement?.focus(), 0);
  }

  closeCalc() {
    this.showCalc = false;
  }

  @HostListener('document:click')
  onDocClick() {
    if (this.showCalc) this.closeCalc();
  }

  onCalcKeydown(ev: KeyboardEvent) {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      const res = this.evaluateCalc(this.calcExpr);
      if (res !== null && isFinite(res)) {
        // Track history
        this.trackCalcExprHistory = this.calcExpr;
        this.trackCalcExprHistoryChange.emit(this.trackCalcExprHistory);
        this.trackCaclResultHistory = res;

        this.accept.emit(res);
        this.closeCalc();
      }
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      this.closeCalc();
    }
  }

  sanitizeCalcExpr() {
    if (!this.calcExpr) { this.calcPreview = null; return; }
    this.calcExpr = this.calcExpr.replace(/\s+/g, '');
    this.calcExpr = this.calcExpr.replace(/[^=0-9+\-*/(),.]/g, '');
    this.calcExpr = '=' + this.calcExpr.replace(/^=+/, '').replace(/=+/g, '');
    this.updateCalcPreview();
  }

  private updateCalcPreview() {
    const res = this.evaluateCalc(this.calcExpr);
    this.calcPreview = (res !== null && isFinite(res)) ? this.numberToLocalized(res) : null;
  }

  private evaluateCalc(expr: string | null | undefined): number | null {
    if (!expr) return null;
    let s = expr.trim();
    if (!s.startsWith('=')) return null;
    s = s.slice(1);

    if (!/^[0-9+\-*/(),.]+$/.test(s)) return null;
    if (/([+\/*]{2,})/.test(s)) return null;

    const jsExpr = s.replace(/\./g, '').replace(/,/g, '.');

    try {
      const fn = new Function(`"use strict"; return (${jsExpr});`);
      const val = fn();
      return (typeof val === 'number' && !isNaN(val)) ? val : null;
    } catch {
      return null;
    }
  }

  private numberToLocalized(n: number): string {
    const [i, d] = String(n).split('.');
    return d !== undefined ? `${i},${d}` : i;
  }
}
